import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950 font-[var(--font-heading)]">
              文章未找到
            </h1>
            <p className="mt-2 text-sm text-foreground-600">你访问的文章可能已被移除或不存在</p>
            <Link
              to="/"
              className="mt-6 inline-block px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Navbar />

      <main className="pt-16 bg-background-50">
        {/* Breadcrumb */}
        <div className="px-6 md:px-10 py-4 border-b border-background-200/70 bg-background-100">
          <nav
            className="mx-auto flex items-center gap-2 text-xs text-foreground-500 flex-wrap"
            style={{ maxWidth: '800px' }}
            aria-label="面包屑导航"
          >
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/category/${article.categoryId}`}
              className="hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {article.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700 truncate">{article.title}</span>
          </nav>
        </div>

        {/* Article Header */}
        <section className="py-10 md:py-14 px-6 md:px-10">
          <article className="mx-auto" style={{ maxWidth: '800px' }}>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
                {article.categoryName}
              </span>
              <span className="text-xs text-foreground-500 whitespace-nowrap">
                {article.readTime} 分钟阅读
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight font-[var(--font-heading)]">
              {article.title}
            </h1>
            <div className="mt-6 flex items-center gap-3">
              <img
                src={article.authorAvatar}
                alt={article.author}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-medium text-foreground-950">{article.author}</p>
                <p className="text-xs text-foreground-500">{article.publishDate}</p>
              </div>
            </div>

            {article.tags.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-8 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div
              className="mt-8 prose prose-sm max-w-none text-foreground-800 leading-relaxed
                [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
                [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
                [&_li]:mb-1
                [&_strong]:font-semibold [&_strong]:text-foreground-950"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Share */}
            <div className="mt-10 pt-6 border-t border-background-200/70 flex items-center gap-4">
              <span className="text-sm text-foreground-600">分享文章：</span>
              <a
                href="#"
                className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors"
                aria-label="分享到微信"
              >
                <i className="ri-wechat-line"></i>
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors"
                aria-label="分享到微博"
              >
                <i className="ri-weibo-line"></i>
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-500 transition-colors"
                aria-label="复制链接"
              >
                <i className="ri-link"></i>
              </a>
            </div>
          </article>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 px-6 md:px-10 bg-background-100">
            <div className="mx-auto" style={{ maxWidth: '1200px' }}>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 mb-8 font-[var(--font-heading)]">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 flex flex-col"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <span className="text-xs text-primary-500 font-medium whitespace-nowrap">
                        {ra.categoryName}
                      </span>
                      <h3 className="mt-1 text-sm font-semibold text-foreground-950 leading-snug line-clamp-2 font-[var(--font-heading)]">
                        {ra.title}
                      </h3>
                      <p className="mt-auto pt-3 text-xs text-foreground-500">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Schema.org */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              image: article.imageUrl,
              author: {
                '@type': 'Person',
                name: article.author,
              },
              datePublished: article.publishDate,
              publisher: {
                '@type': 'Organization',
                name: '男同网',
              },
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://nantongwang.com/' },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: article.categoryName,
                    item: `https://nantongwang.com/category/${article.categoryId}`,
                  },
                  {
                    '@type': 'ListItem',
                    position: 3,
                    name: article.title,
                  },
                ],
              },
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}